
import Vue from 'vue';
import _truncate from 'lodash/truncate';
import _isNil from 'lodash/isNil';
import { SETTING_ACTIONS } from '@/views/Runnits/constants';
import {
	RunnitNodeRun,
	RunnitNodeField,
	RunnitMask,
	isFieldVisibleAndEnabled,
	RUNNIT_NODE_FIELD_TYPE,
	RUNNIT_NODE_RUN_RESULT_TYPE,
	RunnitNodeDef,
	RUNNIT_TYPE,
	extractPromptStringValue,
} from '@run-diffusion/shared';
import RunnitImage from '@/views/Runnits/RunnitImage.vue';
import RunnitVideo from '@/views/Runnits/RunnitVideo.vue';
import RunnitModelSelection from '@/views/Runnits/RunnitSettings/RunnitModelSelection.vue';
import ImageInfoMaskPreview from '@/components/ImageGallery/ImageInfoMaskPreview.vue';
import { mapState, mapActions } from 'vuex';
import { db, storage } from '@/firebase';
import { RunnitRemixMixin } from '@/mixins/RunnitRemixMixin';
import { isPartialLibraryNodeRun } from '@/utils/libraryApi';
import ToolInfoActionBar from './ToolInfoActionBar.vue';
import ClipboardCopy from '@/components/ClipboardCopy.vue';

export default Vue.extend({
	name: 'ImageInfoFields',
	props: {
		// Not `required` and defaulted to []: a run opened from the FILTERED library
		// is a REST partial whose `fields` only arrive when the carousel's lazy
		// Firestore hydration lands (ADR 0018 keeps `fields`/`inputs` out of
		// Postgres). Requiring the prop made that in-flight window throw in
		// fieldsThatShouldDisplay during mount, so the component never mounted and
		// never recovered once the data arrived — the info panel stayed blank.
		fields: { type: Array, default: () => [] },
		nodeRun: { type: Object, required: true },
		insideDrawer: { type: Boolean, default: false },
		insideNode: { type: Boolean, default: false },
		insideCarousel: { type: Boolean, default: false },
		ignoreTruncation: { type: Boolean, default: false },
		index: { type: [String, Number], default: null },
		imageAttributionStyles: { type: Object, default: null },
		hideGeneratingTool: { type: Boolean, default: false },
		resultUuid: { type: String, default: null },
	},
	data () {
		return {
			SETTING_ACTIONS,
			RUNNIT_NODE_FIELD_TYPE,

			showMask: true,
			expandedGroups: {},
			loadingNodeDef: false,

			// Comparison options menu
			comparisonOptionsMenu: false,
			availableComparisonOptions: [],
			currentComparisonField: null,
		};
	},
	mixins: [
		RunnitRemixMixin,
	],
	watch: {
		fields: {
			immediate: true,
			handler (newVal) {
				if (newVal && newVal.length) {
					this.expandedGroups = newVal.reduce((expandedMap, field) => {
						if (field.__rgroup) {
							expandedMap[field.uuid] = true;
						}
						return expandedMap;
					}, {});
				}
			}
		},
		'triggers.imageZoomResetTrigger' () {
			this.comparisonOptionsMenu = false;
			this.currentComparisonField = null;
			this.availableComparisonOptions = [];
		},
		nodeRun: {
			immediate: true,
			async handler (newVal, oldVal) {
				if (oldVal) {
					this.comparisonOptionsMenu = false;
					this.currentComparisonField = null;
					this.availableComparisonOptions = [];
				}

				const needsNodeDefFetch = newVal.nodeDef && (
					typeof newVal.nodeDef === 'string' ||
					!newVal.nodeDef.title ||
					newVal.nodeDef.avatar?.firestore
				);

				this.loadingNodeDef = true;
				try {
					const nodeDefData = needsNodeDefFetch ?
						(await db.doc(`runnitNodeDefs/${newVal.nodeDefId}`).get()).data() :
						newVal.nodeDef;

					let avatar = nodeDefData?.avatar;
					if (nodeDefData && nodeDefData.avatarId && newVal.nodeDef.avatar?.firestore) {
						const avatarDoc = await db.doc(`avatars/${nodeDefData.avatarId}`).get();
						avatar = avatarDoc.exists ? { ...avatarDoc.data(), id: nodeDefData.avatarId } : null;
					}

					this.nodeRun.nodeDef = {
						...nodeDefData,
						id: newVal.nodeDefId,
						avatar,
					};
				} catch (err) {
					console.error('Failed to fetch the generating tool', err);
				} finally {
					this.loadingNodeDef = false;
				}
			}
		},
	},
	computed: {
		...mapState([
			'user',
			'runnitState',
			'triggers',
		]),
		fieldValueTruncateCharCount () {
			let charCount: number = 400;
			if (this.$vuetify.breakpoint.xsOnly || this.insideDrawer) {
				charCount = 100;
			}
			if (this.ignoreTruncation) {
				charCount = 3000;
			}
			return charCount;
		},
		// True only for a library REST partial that has no fields yet, i.e. the
		// carousel's hydration is still in flight. A fully hydrated run with
		// genuinely nothing displayable must NOT show a spinner forever, hence the
		// partial-flag check rather than a bare emptiness test.
		isAwaitingHydration (): boolean {
			return isPartialLibraryNodeRun(this.nodeRun) && !this.fieldsThatShouldDisplay.length;
		},
		fieldsThatShouldDisplay () {
			// Defensive alongside the prop default: a parent passing an explicit
			// `undefined` (e.g. `nodeRun.fields` on an unhydrated partial) bypasses
			// the default, and throwing here breaks the whole panel permanently.
			return (this.fields || []).filter((field) => this.shouldFieldBeDisplayed(field));
		}
	},
	methods: {
		...mapActions([
			'updateRunnitState',
		]),
		extractPromptStringValue,
		_truncate,
		_isNil,
		getImagesForField (field: RunnitNodeField) {
			const input = this.nodeRun.inputs[field.fieldDefUuid];
			if (!input) return [];
			const results = input.nodeRunResults || [];
			const uploads = input.uploads || [];
			return [
				...results.map((r: any) => ({ nodeRunResult: r })),
				...uploads.map((u: any) => ({ upload: u }))
			];
		},
		getMaxImages (field: RunnitNodeField): number | null {
			return this._get(field, 'display.maxImages') || null;
		},
		getStackStyle (index: number) {
			const zIndex = 10 - index;
			const offset = index * 6;

			return {
				zIndex: zIndex,
				bottom: `${offset}px`,
				left: `${offset}px`,
			};
		},
		determineWidth () {
			let width = '100%';
			if (
				this.$vuetify.breakpoint.smAndUp &&
				!this.insideDrawer
			) {
				width = '50%';
			}
			return width;
		},
		getInpaintingMask (field: RunnitNodeField): RunnitMask {
			return (
				field &&
				field.type === RUNNIT_NODE_FIELD_TYPE.IMG &&
				this.nodeRun.inputs[field.fieldDefUuid] &&
				this.nodeRun.inputs[field.fieldDefUuid].inpainting &&
				this.nodeRun.inputs[field.fieldDefUuid].inpainting.mask
			) || null;
		},
		toggleGroupExpansion (uuid) {
			this.expandedGroups[uuid] = !this.expandedGroups[uuid];
		},
		shouldFieldBeDisplayed (field: RunnitNodeField) {
			const isVisibleAndEnabled: boolean = isFieldVisibleAndEnabled(field, this.nodeRun.inputs, true);
			const hideAdmin: boolean = !!(field.adminOnly && !this.user.isAdmin);
			const hasValue = !_isNil(this.nodeRun.inputs[field.fieldDefUuid]); // Show any field that has a value

			return (isVisibleAndEnabled || hasValue) && !hideAdmin;
		},
		routeToTool (runnitNodeDef: RunnitNodeDef) {
			this.onAddRunnitClick(runnitNodeDef, RUNNIT_TYPE.SINGLE_TOOL)
			this.$emit('close-carousel', true);
		},
		showPromptMagic (field: RunnitNodeField) {
			return !!(
				this.nodeRun.promptMagic &&
				this.nodeRun.promptMagic.results &&
				this.nodeRun.promptMagic.results[field.fieldDefUuid] &&
				this.nodeRun.inputs &&
				extractPromptStringValue(this.nodeRun.inputs[field.fieldDefUuid]) !== this.nodeRun.promptMagic.results[field.fieldDefUuid]
			);
		},
		hasImageData (field: RunnitNodeField) {
			const inputData = this.nodeRun.inputs[field.fieldDefUuid];
			return !!(
				(field.type === RUNNIT_NODE_FIELD_TYPE.IMG || field.type === RUNNIT_NODE_FIELD_TYPE.IMGS || field.type === RUNNIT_NODE_FIELD_TYPE.VIDEO) &&
				inputData &&
				(
					inputData.nodeRunResult ||
					inputData.upload ||
					(inputData.image && (inputData.image.nodeRunResult || inputData.image.upload)) ||
					(inputData.video && (inputData.video.nodeRunResult || inputData.video.upload)) ||
					inputData.images ||
					inputData.nodeRunResults
				)
			);
		},
		isVideoResult () {
			const nodeRun: RunnitNodeRun = this.nodeRun;

			return nodeRun?.results?.[0]?.type === RUNNIT_NODE_RUN_RESULT_TYPE.VID;
		},
		isComparisonActive (field: RunnitNodeField) {
			return !!(
				this.runnitState.imageComparisonMode &&
				this.runnitState.comparisonFieldDefUuid === field.fieldDefUuid
			);
		},
		async toggleImageComparison (field: RunnitNodeField) {
			if (this.isComparisonActive(field)) {
				// Turn off comparison mode
				await this.updateRunnitState({
					imageComparisonMode: false,
					comparisonFieldDefUuid: null,
				});
				this.comparisonOptionsMenu = false;
			} else {
				// If menu is open for the same field, close it
				if (this.comparisonOptionsMenu && this.currentComparisonField?.uuid === field.uuid) {
					this.comparisonOptionsMenu = false;
					return;
				}

				const inputData = this.nodeRun.inputs[field.fieldDefUuid];

				// Check if this is a nodeRunResults/uploads case
				if (inputData.nodeRunResults || inputData.uploads) {
					const availableOptions = [];

					try {
						// Collect nodeRunResults first
						if (inputData.nodeRunResults && inputData.nodeRunResults.length > 0) {
							for (const nodeRunResult of inputData.nodeRunResults) {
								const imageUrl = await this.fetchImageUrl(nodeRunResult, null);
								if (imageUrl) {
									availableOptions.push({
										nodeRunResult,
										imageUrl,
										label: `Result ${availableOptions.length + 1}`,
									});
								}
							}
						}

						// Then collect uploads
						if (inputData.uploads && inputData.uploads.length > 0) {
							for (const upload of inputData.uploads) {
								const imageUrl = await this.fetchImageUrl(null, upload);
								if (imageUrl) {
									availableOptions.push({
										upload,
										imageUrl,
										label: `Upload ${availableOptions.length + 1 - (inputData.nodeRunResults?.length || 0)}`,
									});
								}
							}
						}

						// Handle single vs multiple options
						if (availableOptions.length === 1) {
							// Directly enable comparison with the single option
							await this.selectComparisonOption(field, availableOptions[0]);
						} else if (availableOptions.length > 1) {
							// Store options and show menu
							this.availableComparisonOptions = availableOptions;
							this.currentComparisonField = field;
							this.comparisonOptionsMenu = true;
						}
					} catch (error) {
						console.error('Error loading comparison images:', error);
						return;
					}
				} else {
					// Fallback to old logic for other input types
					let sourceImageUrl: string | null = null;

					// First, check if we already have the URL in our map
					if (this.runnitState.comparisonFieldSourceImageUrls[field.fieldDefUuid]) {
						sourceImageUrl = this.runnitState.comparisonFieldSourceImageUrls[field.fieldDefUuid];
					} else {
						try {
							// Handle different input data structures
							let nodeRunResult = null;
							let upload = null;

							if (inputData.nodeRunResult) {
								nodeRunResult = inputData.nodeRunResult;
							} else if (inputData.upload) {
								upload = inputData.upload;
							} else if (inputData.image) {
								if (inputData.image.nodeRunResult) {
									nodeRunResult = inputData.image.nodeRunResult;
								} else if (inputData.image.upload) {
									upload = inputData.image.upload;
								}
							} else if (inputData.video) {
								if (inputData.video.nodeRunResult) {
									nodeRunResult = inputData.video.nodeRunResult;
								} else if (inputData.video.upload) {
									upload = inputData.video.upload;
								}
							}

							// Fetch the image URL
							if (nodeRunResult || upload) {
								sourceImageUrl = await this.fetchImageUrl(nodeRunResult, upload);
							}
						} catch (error) {
							console.error('Error loading comparison image:', error);
							// Handle error - maybe show a snackbar
							return;
						}
					}

					if (sourceImageUrl) {
						await this.updateRunnitState({
							imageComparisonMode: true,
							comparisonFieldDefUuid: field.fieldDefUuid,
							comparisonFieldSourceImageUrls: {
								...this.runnitState.comparisonFieldSourceImageUrls,
								[field.fieldDefUuid]: sourceImageUrl,
							},
						});
					}
				}
			}
		},
		async fetchImageUrl (nodeRunResult: any, upload: any): Promise<string | null> {
			let filePath: string;

			if (nodeRunResult) {
				// Get file path for node run result
				const { getRunnitNodeRunResultFilePath } = await import('@run-diffusion/shared');
				const filePathData = getRunnitNodeRunResultFilePath(nodeRunResult);
				filePath = filePathData.filePath;
			} else if (upload) {
				// Get file path for upload
				const { getRunnitUploadFilePath } = await import('@run-diffusion/shared');
				const filePathData = getRunnitUploadFilePath(upload);
				filePath = filePathData.filePath;
			} else {
				return null;
			}

			// Fetch URL from Firebase Storage
			const fileRef = storage.ref(filePath);
			return await fileRef.getDownloadURL();
		},
		async selectComparisonOption (field: RunnitNodeField, option: any) {
			await this.updateRunnitState({
				imageComparisonMode: true,
				comparisonFieldDefUuid: field.fieldDefUuid,
				comparisonFieldSourceImageUrls: {
					...this.runnitState.comparisonFieldSourceImageUrls,
					[field.fieldDefUuid]: option.imageUrl,
				},
			});

			// Close the options menu
			this.comparisonOptionsMenu = false;
		},
	},
	components: {
		ToolInfoActionBar,
		ImageInfoMaskPreview,
		RunnitModelSelection,
		RunnitImage,
		RunnitVideo,
		ClipboardCopy,
		MenuOrBottomSheet: () => import('@/components/base/MenuOrBottomSheet.vue'),
	},
});
